import type { ComponentChildren } from 'preact';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import {
  COUNTRIES,
  DELIVERY_AREAS,
  LEAD_TIMES,
  MAX_DOC_BYTES,
  PAYMENT_TERMS,
  SECTIONS,
  SUPPLY_TYPES,
  registrationProblems,
  sectionProgress,
  type AnswerKey,
  type Answers,
  type DocFiles,
  type DocSlot,
  type Problem,
  type RegisterFileBody,
  type RegisterOpenBody,
  type RegisterSavedBody,
  type RegisterSubmittedBody,
  crInput,
  needsNationalAddress,
} from '../../shared/suppliers/registration';
import { useI18n, type MessageKey } from '../i18n';
import { ApiError } from '../lib/api';
import { formatDate } from '../lib/dates';
import { registerSend, registerUpload } from '../lib/register-api';
import { loadTurnstile, type TurnstileApi } from '../lib/turnstile';

// Public supplier registration (screen 21, P2.4). Opened from an invite link; no sign-in. The
// browser checks answers only to guide the supplier; the broker checks everything again.

type Load =
  | { kind: 'loading' }
  | { kind: 'refused'; code: string; reference: string | null }
  | { kind: 'ok'; data: RegisterOpenBody }
  | { kind: 'done'; supplierId: string };

const REFUSALS = new Set([
  'invite_invalid',
  'invite_expired',
  'invite_revoked',
  'invite_submitted',
]);
const DOCS: { slot: DocSlot; required: 'yes' | 'sa' | 'no' }[] = [
  { slot: 'cr', required: 'yes' },
  { slot: 'vat', required: 'yes' },
  { slot: 'nationalAddress', required: 'sa' },
  { slot: 'profile', required: 'no' },
  { slot: 'priceList', required: 'no' },
  { slot: 'bankLetter', required: 'yes' },
];

const today = () =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Riyadh' }).format(new Date());
const kb = (n: number) => `${Math.max(1, Math.round(n / 1024))} KB`;

export function RegisterPage({ token }: { token: string }) {
  const { t, lang } = useI18n();
  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [answers, setAnswers] = useState<Answers | null>(null);
  const [files, setFiles] = useState<DocFiles>({});
  const [tried, setTried] = useState(false);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<'save' | 'submit' | DocSlot | null>(null);
  const [notice, setNotice] = useState<{ ok: boolean; key: MessageKey; ref?: string } | null>(null);
  const [fileErrors, setFileErrors] = useState<Partial<Record<DocSlot, string>>>({});
  const [human, setHuman] = useState<string>('');
  const [botState, setBotState] = useState<'loading' | 'ready' | 'failed'>('loading');
  const botBox = useRef<HTMLDivElement>(null);
  const bot = useRef<{ api: TurnstileApi; id: string } | null>(null);
  const summary = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!token) {
      setLoad({ kind: 'refused', code: 'invite_invalid', reference: null });
      return;
    }
    let live = true;
    registerSend<RegisterOpenBody>('open', token)
      .then((data) => {
        if (!live) return;
        setAnswers(data.answers);
        setFiles(data.files);
        setLoad({ kind: 'ok', data });
      })
      .catch((err: unknown) => {
        if (!live) return;
        const e = err instanceof ApiError ? err : null;
        setLoad({
          kind: 'refused',
          code: e?.code ?? 'generic',
          reference: e?.correlationId ?? null,
        });
      });
    return () => {
      live = false;
    };
  }, [token]);

  const siteKey = load.kind === 'ok' ? load.data.siteKey : '';
  useEffect(() => {
    if (!siteKey || !botBox.current) return;
    let live = true;
    const box = botBox.current;
    setHuman('');
    setBotState('loading');
    loadTurnstile()
      .then((api) => {
        if (!live) return;
        const id = api.render(box, {
          sitekey: siteKey,
          action: 'register',
          language: lang,
          theme: 'light',
          callback: (value: string) => setHuman(value),
          'expired-callback': () => setHuman(''),
          'error-callback': () => setBotState('failed'),
        });
        bot.current = { api, id };
        setBotState('ready');
      })
      .catch(() => live && setBotState('failed'));
    return () => {
      live = false;
      if (bot.current) bot.current.api.remove(bot.current.id);
      bot.current = null;
    };
  }, [siteKey, lang]);

  const categories = load.kind === 'ok' ? load.data.categories : [];
  const problems = useMemo(
    () =>
      answers
        ? registrationProblems(answers, files, today(), new Set(categories.map((c) => c.id)))
        : {},
    [answers, files, categories],
  );
  const progress = sectionProgress(problems);

  if (load.kind === 'loading') {
    return (
      <section class="card" aria-busy="true">
        <p>{t('reg.loading')}</p>
      </section>
    );
  }
  if (load.kind === 'refused') {
    const code = REFUSALS.has(load.code) ? load.code : 'generic';
    return (
      <section class="card card--error" role="alert" data-testid="register-refused">
        <h2 class="card__title">{t(`reg.refused.${code}.title` as MessageKey)}</h2>
        <p>{t(`reg.refused.${code}.body` as MessageKey)}</p>
        {load.reference && (
          <p class="caption" dir="ltr">
            {t('error.reference', { ref: load.reference })}
          </p>
        )}
      </section>
    );
  }
  if (load.kind === 'done') {
    return (
      <section class="card card--emphasis stack" role="status" data-testid="register-done">
        <h2 class="card__title">{t('reg.done.title')}</h2>
        <p>
          {t('reg.done.ref')}{' '}
          <span class="ref" dir="ltr">
            {load.supplierId}
          </span>
        </p>
        <ol class="steps-list">
          {(['1', '2', '3', '4'] as const).map((n) => (
            <li key={n}>{t(`reg.next.${n}` as MessageKey)}</li>
          ))}
        </ol>
      </section>
    );
  }
  if (!answers) return null;
  const a = answers;
  const saudiCr = a.country === 'SA' || a.country === '';
  const set = <K extends AnswerKey>(k: K, v: Answers[K]) => {
    setAnswers({ ...a, [k]: v });
    if (serverErrors[k]) {
      const { [k]: _gone, ...rest } = serverErrors;
      void _gone;
      setServerErrors(rest);
    }
  };
  const toggle = <K extends 'categories' | 'deliveryAreas' | 'paymentTerms'>(
    k: K,
    v: Answers[K][number],
  ) => {
    const list = a[k] as string[];
    set(k, (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]) as Answers[K]);
  };

  const errorOf = (k: string): string | null => {
    const code = serverErrors[k] ?? (tried ? problems[k as AnswerKey] : undefined);
    return code ? t(`reg.err.${code as Problem}` as MessageKey) : null;
  };

  // A plain function, not a component: a component declared here would remount (and lose
  // focus) on every keystroke.
  const field = (
    k: AnswerKey,
    label: MessageKey,
    control: (attrs: Record<string, unknown>) => ComponentChildren,
    opts: { optional?: boolean | undefined; hint?: MessageKey | undefined } = {},
  ) => {
    const id = `reg-${k}`;
    const err = errorOf(k);
    const described = [opts.hint && `${id}-hint`, err && `${id}-err`].filter(Boolean).join(' ');
    return (
      <div class="field">
        <label for={id}>
          {t(label)}
          {opts.optional && <span class="caption"> {t('reg.optional')}</span>}
        </label>
        {control({
          id,
          'aria-invalid': err ? true : undefined,
          'aria-describedby': described || undefined,
        })}
        {opts.hint && (
          <p class="caption" id={`${id}-hint`}>
            {t(opts.hint)}
          </p>
        )}
        {err && (
          <p class="field__error" id={`${id}-err`}>
            {err}
          </p>
        )}
      </div>
    );
  };

  const text = (
    k: Exclude<
      AnswerKey,
      'categories' | 'deliveryAreas' | 'paymentTerms' | 'accurate' | 'pdpl' | 'conduct'
    >,
    label: MessageKey,
    opts: {
      optional?: boolean | undefined;
      hint?: MessageKey | undefined;
      type?: string;
      ltr?: boolean;
      max?: number;
      auto?: string;
    } = {},
  ) =>
    field(
      k,
      label,
      (attrs) => (
        <input
          {...attrs}
          type={opts.type ?? 'text'}
          dir={opts.ltr ? 'ltr' : 'auto'}
          maxLength={opts.max ?? 200}
          autoComplete={opts.auto ?? 'off'}
          value={a[k] as string}
          onInput={(e) => set(k, (e.target as HTMLInputElement).value as Answers[typeof k])}
        />
      ),
      opts,
    );

  const radios = <K extends 'supplyType' | 'vatRegistered' | 'related'>(
    k: K,
    label: MessageKey,
    values: readonly Exclude<Answers[K], ''>[],
  ) => {
    const err = errorOf(k);
    return (
      <fieldset class="reg-group">
        <legend>{t(label)}</legend>
        <div class="reg-segment" role="group">
          {values.map((v) => (
            <button
              key={v}
              type="button"
              class="chip"
              aria-pressed={a[k] === v}
              onClick={() => set(k, v as Answers[K])}
            >
              {t(`reg.${k}.${v}` as MessageKey)}
            </button>
          ))}
        </div>
        {err && <p class="field__error">{err}</p>}
      </fieldset>
    );
  };

  const chips = <K extends 'deliveryAreas' | 'paymentTerms'>(
    k: K,
    label: MessageKey,
    values: readonly Answers[K][number][],
  ) => {
    const err = errorOf(k);
    return (
      <fieldset class="reg-group">
        <legend>{t(label)}</legend>
        <div class="reg-chips">
          {values.map((v) => (
            <button
              key={v}
              type="button"
              class="chip"
              aria-pressed={(a[k] as string[]).includes(v)}
              onClick={() => toggle(k, v)}
            >
              {t(`reg.${k}.${v}` as MessageKey)}
            </button>
          ))}
        </div>
        {err && <p class="field__error">{err}</p>}
      </fieldset>
    );
  };

  const check = (k: 'accurate' | 'pdpl' | 'conduct', extra?: ComponentChildren) => {
    const err = errorOf(k);
    return (
      <div>
        <div class="field field--check">
          <input
            id={`reg-${k}`}
            type="checkbox"
            checked={a[k]}
            aria-invalid={err ? true : undefined}
            onChange={(e) => set(k, (e.target as HTMLInputElement).checked)}
          />
          <label for={`reg-${k}`}>{t(`reg.f.${k}` as MessageKey)}</label>
        </div>
        {extra}
        {err && <p class="field__error">{err}</p>}
      </div>
    );
  };

  async function save() {
    setBusy('save');
    setNotice(null);
    try {
      await registerSend<RegisterSavedBody>('draft', token, { answers: a });
      setNotice({ ok: true, key: 'reg.saved' });
    } catch (err) {
      fail(err);
    } finally {
      setBusy(null);
    }
  }

  function fail(err: unknown) {
    const e = err instanceof ApiError ? err : null;
    if (e && REFUSALS.has(e.code)) {
      setLoad({ kind: 'refused', code: e.code, reference: e.correlationId });
      return;
    }
    if (e?.code === 'invalid_input') setServerErrors(e.fields);
    const key = (
      ['invalid_input', 'bot_check'].includes(e?.code ?? '')
        ? `reg.error.${e?.code ?? ''}`
        : 'reg.error.generic'
    ) as MessageKey;
    setNotice({ ok: false, key, ...(e?.correlationId ? { ref: e.correlationId } : {}) });
  }

  async function upload(slot: DocSlot, file: File | undefined) {
    if (!file) return;
    setFileErrors({ ...fileErrors, [slot]: undefined });
    if (file.size > MAX_DOC_BYTES) {
      setFileErrors({ ...fileErrors, [slot]: t('reg.err.fileSize') });
      return;
    }
    setBusy(slot);
    try {
      const out = await registerUpload<RegisterFileBody>(slot, token, file);
      setFiles((f) => ({ ...f, [slot]: out.file }));
    } catch (err) {
      const e = err instanceof ApiError ? err : null;
      if (e && REFUSALS.has(e.code)) return fail(err);
      const code = e?.fields.file;
      setFileErrors({
        ...fileErrors,
        [slot]:
          code && ['fileType', 'fileSize', 'fileEmpty', 'fileContent'].includes(code)
            ? t(`reg.err.${code}` as MessageKey)
            : t('reg.error.generic'),
      });
    } finally {
      setBusy(null);
    }
  }

  async function submit() {
    setTried(true);
    setNotice(null);
    if (Object.keys(problems).length > 0) {
      setNotice({ ok: false, key: 'reg.error.fix' });
      summary.current?.focus();
      return;
    }
    if (!human) {
      setNotice({ ok: false, key: 'reg.error.human' });
      return;
    }
    setBusy('submit');
    try {
      const out = await registerSend<RegisterSubmittedBody>('submit', token, {
        answers: a,
        turnstile: human,
      });
      setLoad({ kind: 'done', supplierId: out.supplierId });
      scrollTo(0, 0);
    } catch (err) {
      // The check is single use: get a fresh one for the next try.
      setHuman('');
      if (bot.current) bot.current.api.reset(bot.current.id);
      fail(err);
    } finally {
      setBusy(null);
    }
  }

  const catName = (c: (typeof categories)[number]) =>
    lang === 'ar' ? c.nameAR || c.nameEN : c.nameEN;
  const goods = categories.filter((c) => c.type !== 'service');
  const services = categories.filter((c) => c.type === 'service');
  const sectionTitle = (i: number) =>
    `${i + 1}. ${t(`reg.s.${SECTIONS[i]?.id ?? ''}` as MessageKey)}`;
  const doneCount = Object.values(progress).filter((n) => n === 0).length;

  return (
    <div class="pr-layout">
      <form class="stack" noValidate onSubmit={(e) => (e.preventDefault(), void submit())}>
        <p class="caption">{t('reg.intro')}</p>
        {load.data.note && (
          <section class="card card--emphasis" aria-labelledby="reg-note">
            <h2 id="reg-note" class="card__title">
              {t('reg.note.title')}
            </h2>
            <p dir="auto">{load.data.note}</p>
          </section>
        )}

        <section class="card stack" aria-labelledby="reg-s1">
          <h2 id="reg-s1" class="card__title">
            {sectionTitle(0)}
          </h2>
          <div class="reg-grid">
            {text('legalNameEN', 'reg.f.legalNameEN', { auto: 'organization' })}
            {text('legalNameAR', 'reg.f.legalNameAR', { optional: true })}
            {text('tradingName', 'reg.f.tradingName', { optional: true })}
            {field('country', 'reg.f.country', (attrs) => (
              <select
                {...attrs}
                value={a.country}
                onChange={(e) =>
                  set('country', (e.target as HTMLSelectElement).value as Answers['country'])
                }
              >
                <option value="">{t('pr.select')}</option>
                {COUNTRIES.map((c) => (
                  <option key={c} value={c}>
                    {t(`reg.country.${c}` as MessageKey)}
                  </option>
                ))}
              </select>
            ))}
            {text('city', 'reg.f.city', { max: 100 })}
            {text('website', 'reg.f.website', { optional: true, ltr: true, type: 'url' })}
          </div>
          {text('address', 'reg.f.address', { optional: true, max: 300 })}
        </section>

        <section class="card stack" aria-labelledby="reg-s2">
          <h2 id="reg-s2" class="card__title">
            {sectionTitle(1)}
          </h2>
          <div class="reg-grid">
            {field(
              'crNumber',
              'reg.f.crNumber',
              (attrs) => (
                <input
                  {...attrs}
                  type="text"
                  dir="ltr"
                  inputMode={saudiCr ? 'numeric' : 'text'}
                  maxLength={saudiCr ? 10 : 30}
                  autoComplete="off"
                  value={a.crNumber}
                  onInput={(e) => {
                    const input = e.target as HTMLInputElement;
                    const kept = crInput(a.country, input.value);
                    // Keep the box showing exactly what is stored (digits only for Saudi CRs).
                    if (input.value !== kept) input.value = kept;
                    set('crNumber', kept);
                  }}
                />
              ),
              {
                hint:
                  a.country === 'SA' ? 'reg.hint.crSa' : saudiCr ? 'reg.hint.crDigits' : undefined,
              },
            )}
            {text('crExpiry', 'reg.f.crExpiry', { optional: true, type: 'date', ltr: true })}
          </div>
          {text('vatNumber', 'reg.f.vatNumber', {
            ltr: true,
            max: 30,
            hint: a.country === 'SA' ? 'reg.hint.vatSa' : undefined,
          })}
        </section>

        <section class="card stack" aria-labelledby="reg-s3">
          <h2 id="reg-s3" class="card__title">
            {sectionTitle(2)}
          </h2>
          <div class="reg-grid">
            {text('contactName', 'reg.f.contactName', { auto: 'name', max: 120 })}
            {text('contactRole', 'reg.f.contactRole', { optional: true, max: 120 })}
            {text('contactEmail', 'reg.f.contactEmail', {
              type: 'email',
              ltr: true,
              auto: 'email',
              max: 120,
            })}
            {text('contactMobile', 'reg.f.contactMobile', {
              type: 'tel',
              ltr: true,
              auto: 'tel',
              max: 30,
              hint: 'reg.hint.phone',
            })}
            {text('contactWhatsApp', 'reg.f.contactWhatsApp', {
              optional: true,
              type: 'tel',
              ltr: true,
              max: 30,
            })}
            {text('financeEmail', 'reg.f.financeEmail', {
              optional: true,
              type: 'email',
              ltr: true,
              max: 120,
            })}
          </div>
        </section>

        <section class="card stack" aria-labelledby="reg-s4">
          <h2 id="reg-s4" class="card__title">
            {sectionTitle(3)}
          </h2>
          <p class="caption">{t('reg.supply.intro')}</p>
          {radios('supplyType', 'reg.f.supplyType', SUPPLY_TYPES)}
          <fieldset class="reg-group">
            <legend>{t('reg.f.categories')}</legend>
            {[
              ['reg.cat.goods', goods],
              ['reg.cat.services', services],
            ].map(([title, list]) =>
              (list as typeof categories).length === 0 ? null : (
                <div key={title as string} class="stack">
                  <p class="reg-subhead">{t(title as MessageKey)}</p>
                  <div class="reg-chips">
                    {(list as typeof categories).map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        class="chip"
                        aria-pressed={a.categories.includes(c.id)}
                        onClick={() => toggle('categories', c.id)}
                      >
                        {catName(c)}
                      </button>
                    ))}
                  </div>
                </div>
              ),
            )}
            <p class="notice notice--info">
              {t('reg.cat.count', { n: String(a.categories.length) })}
            </p>
            {errorOf('categories') && <p class="field__error">{errorOf('categories')}</p>}
          </fieldset>
          {field('description', 'reg.f.description', (attrs) => (
            <textarea
              {...attrs}
              rows={3}
              dir="auto"
              maxLength={1000}
              value={a.description}
              onInput={(e) => set('description', (e.target as HTMLTextAreaElement).value)}
            />
          ))}
          <div class="reg-grid">
            {text('brands', 'reg.f.brands', { optional: true, max: 300 })}
            {field('leadTime', 'reg.f.leadTime', (attrs) => (
              <select
                {...attrs}
                value={a.leadTime}
                onChange={(e) =>
                  set('leadTime', (e.target as HTMLSelectElement).value as Answers['leadTime'])
                }
              >
                <option value="">{t('pr.select')}</option>
                {LEAD_TIMES.map((v) => (
                  <option key={v} value={v}>
                    {t(`reg.leadTime.${v}` as MessageKey)}
                  </option>
                ))}
              </select>
            ))}
          </div>
          {chips('deliveryAreas', 'reg.f.deliveryAreas', DELIVERY_AREAS)}
          {chips('paymentTerms', 'reg.f.paymentTerms', PAYMENT_TERMS)}
        </section>

        <section class="card stack" aria-labelledby="reg-s5">
          <h2 id="reg-s5" class="card__title">
            {sectionTitle(4)}
          </h2>
          <ul class="reg-docs">
            {DOCS.map(({ slot, required: rule }) => {
              const required =
                rule === 'sa' ? (needsNationalAddress(a.country) ? 'yes' : 'no') : rule;
              const stored = files[slot];
              const err =
                fileErrors[slot] ??
                (tried && problems[`doc.${slot}`] ? t('reg.err.required') : null);
              const id = `reg-doc-${slot}`;
              return (
                <li key={slot} class={`reg-doc${stored ? ' reg-doc--done' : ''}`}>
                  <label for={id} class="reg-doc__label">
                    {t(`reg.doc.${slot}` as MessageKey)}
                    {required !== 'no' ? (
                      <span class="reg-required" aria-hidden="true">
                        {' '}
                        *
                      </span>
                    ) : null}
                  </label>
                  <span class="reg-doc__state caption" id={`${id}-state`}>
                    {busy === slot ? (
                      t('reg.doc.uploading')
                    ) : stored ? (
                      <span dir="ltr">
                        {stored.name} · {kb(stored.size)}
                      </span>
                    ) : (
                      t(required === 'no' ? 'reg.doc.optionalHint' : 'reg.doc.hint')
                    )}
                  </span>
                  <input
                    id={id}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    aria-describedby={`${id}-state${err ? ` ${id}-err` : ''}`}
                    aria-invalid={err ? true : undefined}
                    disabled={busy !== null}
                    onChange={(e) => {
                      const input = e.target as HTMLInputElement;
                      void upload(slot, input.files?.[0]);
                      input.value = '';
                    }}
                  />
                  {err && (
                    <p class="field__error" id={`${id}-err`}>
                      {err}
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
          <div class="reg-grid">
            {text('bankName', 'reg.f.bankName', { max: 120 })}
            {text('accountHolder', 'reg.f.accountHolder', { hint: 'reg.hint.holder' })}
            {text('iban', 'reg.f.iban', { ltr: true, max: 42, hint: 'reg.hint.iban' })}
            {text('swift', 'reg.f.swift', {
              ltr: true,
              max: 15,
              optional: a.country === 'SA',
              hint: 'reg.hint.swift',
            })}
          </div>
          <p class="notice notice--info">{t('reg.bankNote')}</p>
        </section>

        <section class="card stack" aria-labelledby="reg-s6">
          <h2 id="reg-s6" class="card__title">
            {sectionTitle(5)}
          </h2>
          {radios('related', 'reg.f.related', ['no', 'yes'])}
          {a.related === 'yes' &&
            field('relatedDetails', 'reg.f.relatedDetails', (attrs) => (
              <textarea
                {...attrs}
                rows={2}
                dir="auto"
                maxLength={500}
                value={a.relatedDetails}
                onInput={(e) => set('relatedDetails', (e.target as HTMLTextAreaElement).value)}
              />
            ))}
          {check('accurate')}
          {check('pdpl')}
          {check(
            'conduct',
            <details class="reg-conduct">
              <summary>{t('reg.conduct.read')}</summary>
              <p class="pill pill--warning">{t('reg.conduct.draft')}</p>
              <ul>
                {(['1', '2', '3', '4', '5'] as const).map((n) => (
                  <li key={n}>{t(`reg.conduct.${n}` as MessageKey)}</li>
                ))}
              </ul>
            </details>,
          )}
        </section>

        <div class="card stack reg-submit">
          <div ref={summary} tabIndex={-1} aria-live="polite">
            {notice && (
              <p
                class={notice.ok ? 'form-saved' : 'form-problem'}
                role={notice.ok ? 'status' : 'alert'}
              >
                {/* Kept as a key so it follows a language switch; the count stays live. */}
                {t(notice.key, { n: String(Object.keys(problems).length) })}
                {notice.ref && ` ${t('error.reference', { ref: notice.ref })}`}
              </p>
            )}
          </div>
          <div class="reg-bot">
            <div ref={botBox} data-testid="turnstile" />
            {botState === 'loading' && <p class="caption">{t('reg.bot.loading')}</p>}
            {botState === 'failed' && <p class="field__error">{t('reg.bot.failed')}</p>}
            {human && <p class="caption">{t('reg.bot.passed')}</p>}
          </div>
          <div class="actions">
            <button
              type="button"
              class="button"
              disabled={busy !== null}
              onClick={() => void save()}
            >
              {busy === 'save' ? t('reg.saving') : t('reg.save')}
            </button>
            <button type="submit" class="button button--primary" disabled={busy !== null}>
              {busy === 'submit' ? t('reg.submitting') : t('reg.submit')}
            </button>
          </div>
        </div>
      </form>

      <aside class="stack">
        <section class="card" aria-labelledby="reg-inv">
          <h2 id="reg-inv" class="facts__label">
            {t('reg.invite.title')}
          </h2>
          <p>
            <strong>{t('reg.invite.by')}</strong>
          </p>
          <p class="caption">
            {t('reg.invite.for', {
              company: load.data.company,
              date: formatDate(load.data.expiresAt, lang),
            })}
          </p>
          <p class="caption">{t('reg.invite.help')}</p>
        </section>
        <section class="card" aria-labelledby="reg-progress">
          <h2 id="reg-progress" class="facts__label">
            {t('reg.progress', { done: String(doneCount), total: String(SECTIONS.length) })}
          </h2>
          <ol class="reg-progress">
            {SECTIONS.map((s, i) => (
              <li key={s.id}>
                <a
                  href={`#reg-s${i + 1}`}
                  onClick={(e) => {
                    e.preventDefault();
                    document
                      .getElementById(`reg-s${i + 1}`)
                      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                >
                  {sectionTitle(i)}
                </a>
                <span class={progress[s.id] === 0 ? 'reg-ok' : 'reg-todo'}>
                  {progress[s.id] === 0
                    ? t('reg.state.done')
                    : t('reg.state.todo', { n: String(progress[s.id]) })}
                </span>
              </li>
            ))}
          </ol>
        </section>
        <section class="card card--info" aria-labelledby="reg-next">
          <h2 id="reg-next" class="facts__label">
            {t('reg.next.title')}
          </h2>
          <ol class="steps-list">
            {(['1', '2', '3', '4'] as const).map((n) => (
              <li key={n}>{t(`reg.next.${n}` as MessageKey)}</li>
            ))}
          </ol>
        </section>
      </aside>
    </div>
  );
}
